import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/dashboard/Layout';
import { Cpu, HardDrive, Monitor, TrendingUp, DollarSign, Package, Bot, Search, Filter } from 'lucide-react';
import axios from 'axios';

const AssetAnalyticsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [quickStats, setQuickStats] = useState(null);
  const [byProcessor, setByProcessor] = useState([]);
  const [byRAM, setByRAM] = useState([]);
  const [byCategory, setByCategory] = useState([]);
  const [pricingAnalysis, setPricingAnalysis] = useState([]);
  const [showAIChat, setShowAIChat] = useState(false);
  const [aiQuery, setAIQuery] = useState('');
  const [aiResponse, setAIResponse] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState({});

  const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [stats, processor, ram, category, pricing] = await Promise.all([
        axios.get(`${API_BASE}/analytics-detailed/assets/quick-stats`, config),
        axios.get(`${API_BASE}/analytics-detailed/assets/by-processor`, config),
        axios.get(`${API_BASE}/analytics-detailed/assets/by-ram`, config),
        axios.get(`${API_BASE}/analytics-detailed/assets/by-category-detailed`, config),
        axios.get(`${API_BASE}/analytics-detailed/assets/rental-pricing-analysis`, config)
      ]);

      setQuickStats(stats.data);
      setByProcessor(processor.data);
      setByRAM(ram.data);
      setByCategory(category.data);
      setPricingAnalysis(pricing.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAIQuery = async () => {
    if (!aiQuery.trim()) return;

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      // Parse query for AI recommendation
      const response = await axios.post(
        `${API_BASE}/analytics-detailed/ai/asset-recommendation`,
        { need: aiQuery, budget: 10000000, duration: 'daily' },
        config
      );

      setAIResponse(response.data);
    } catch (error) {
      console.error('AI query failed:', error);
      setAIResponse({ error: 'Failed to get AI recommendation' });
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatPercentage = (value) => {
    return `${Math.round(value || 0)}%`;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <p className="text-muted-foreground">Loading analytics...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Asset Analytics & Intelligence</h1>
            <p className="text-muted-foreground">Data lengkap untuk decision making cepat</p>
          </div>
          <button
            onClick={() => setShowAIChat(!showAIChat)}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 flex items-center space-x-2"
          >
            <Bot className="w-5 h-5" />
            <span>Tanya AI Assistant</span>
          </button>
        </div>

        {/* AI Chat Widget */}
        {showAIChat && (
          <div className="glass-card p-4 sm:p-6 rounded-xl border border-primary/40">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
              <Bot className="w-5 h-5 mr-2 text-primary" />
              AI Assistant - Quick Insights
            </h3>
            <div className="flex flex-col sm:flex-row gap-2 mb-4">
              <input
                type="text"
                value={aiQuery}
                onChange={(e) => setAIQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAIQuery()}
                placeholder="Contoh: Saya butuh setup untuk presentasi client, budget 5 juta"
                className="flex-1 px-4 py-2 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary"
              />
              <button
                onClick={handleAIQuery}
                className="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
              >
                <Search className="w-5 h-5 inline mr-2" />
                Cari
              </button>
            </div>

            {aiResponse && (
              <div className="bg-muted/30 p-4 rounded-lg">
                <p className="text-foreground font-medium mb-2">
                  ✨ Rekomendasi: {aiResponse.recommendation_reason}
                </p>
                <p className="text-sm text-muted-foreground mb-3">
                  Tersedia: {aiResponse.available_assets?.length || 0} unit
                </p>
                {aiResponse.available_assets?.slice(0, 3).map((asset, idx) => (
                  <div key={idx} className="bg-background/50 p-3 rounded mb-2">
                    <p className="font-medium text-foreground">
                      {asset.brand} {asset.model}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {asset.processor} | {asset.ram} | {asset.storage}
                    </p>
                    <p className="text-sm text-primary font-medium mt-1">
                      {formatCurrency(asset.daily_rate)}/hari
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Quick Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="glass-card p-4 sm:p-6 rounded-xl border border-border/40">
            <div className="flex items-center justify-between mb-2">
              <Package className="w-8 h-8 text-blue-500" />
              <span className="text-2xl sm:text-3xl font-bold text-foreground">
                {quickStats?.total_assets || 0}
              </span>
            </div>
            <p className="text-sm text-muted-foreground">Total Assets</p>
            <p className="text-xs text-green-600 mt-1">
              {quickStats?.ready_units || 0} ready untuk disewa
            </p>
          </div>

          <div className="glass-card p-4 sm:p-6 rounded-xl border border-border/40">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-8 h-8 text-green-500" />
              <span className="text-2xl sm:text-3xl font-bold text-foreground">
                {formatPercentage(quickStats?.utilization_rate)}
              </span>
            </div>
            <p className="text-sm text-muted-foreground">Utilization Rate</p>
            <p className="text-xs text-muted-foreground mt-1">
              {quickStats?.rented_units || 0} sedang disewa
            </p>
          </div>

          <div className="glass-card p-4 sm:p-6 rounded-xl border border-border/40">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="w-8 h-8 text-yellow-500" />
              <span className="text-lg sm:text-xl font-bold text-foreground">
                {formatCurrency(quickStats?.revenue_potential?.total_daily_potential)}
              </span>
            </div>
            <p className="text-sm text-muted-foreground">Daily Revenue Potential</p>
            <p className="text-xs text-muted-foreground mt-1">
              Assets yang ready
            </p>
          </div>

          <div className="glass-card p-4 sm:p-6 rounded-xl border border-border/40">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="w-8 h-8 text-purple-500" />
              <span className="text-lg sm:text-xl font-bold text-foreground">
                {formatCurrency(quickStats?.revenue_potential?.total_monthly_potential)}
              </span>
            </div>
            <p className="text-sm text-muted-foreground">Monthly Revenue Potential</p>
            <p className="text-xs text-green-600 mt-1">
              Potensi maksimal
            </p>
          </div>
        </div>

        {/* Top Configurations */}
        {quickStats?.top_configurations && quickStats.top_configurations.length > 0 && (
          <div className="glass-card p-4 sm:p-6 rounded-xl border border-border/40">
            <h3 className="text-lg font-semibold text-foreground mb-4">
              🏆 Konfigurasi Populer (Paling Banyak Tersedia)
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {quickStats.top_configurations.map((config, idx) => (
                <div key={idx} className="bg-muted/30 p-4 rounded-lg border border-border">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-2xl font-bold text-primary">#{idx + 1}</span>
                    <span className="px-2 py-1 bg-green-500/10 text-green-600 rounded text-sm font-medium">
                      {config.ready} Ready
                    </span>
                  </div>
                  <p className="font-medium text-foreground">{config._id.processor}</p>
                  <p className="text-sm text-muted-foreground">{config._id.ram}</p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Total: {config.count} unit
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Breakdown by Processor */}
        <div className="glass-card p-4 sm:p-6 rounded-xl border border-border/40">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
            <Cpu className="w-5 h-5 mr-2 text-blue-500" />
            Detail Per Processor (Untuk Client Meeting)
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-3 text-foreground font-medium">Processor</th>
                  <th className="text-center p-3 text-foreground font-medium">Total</th>
                  <th className="text-center p-3 text-foreground font-medium">Ready</th>
                  <th className="text-center p-3 text-foreground font-medium">Rented</th>
                  <th className="text-center p-3 text-foreground font-medium">On Duty</th>
                  <th className="text-right p-3 text-foreground font-medium">Avg Harga/Hari</th>
                  <th className="text-right p-3 text-foreground font-medium">Avg Harga/Bulan</th>
                  <th className="text-right p-3 text-foreground font-medium">Total Value</th>
                </tr>
              </thead>
              <tbody>
                {byProcessor.map((item, idx) => (
                  <tr key={idx} className="border-b border-border hover:bg-muted/30 cursor-pointer">
                    <td className="p-3 font-medium text-foreground">{item._id}</td>
                    <td className="p-3 text-center">
                      <span className="px-2 py-1 bg-blue-500/10 text-blue-600 rounded font-medium">
                        {item.total_units}
                      </span>
                    </td>
                    <td className="p-3 text-center">
                      <span className="px-2 py-1 bg-green-500/10 text-green-600 rounded font-medium">
                        {item.ready}
                      </span>
                    </td>
                    <td className="p-3 text-center text-muted-foreground">{item.rented}</td>
                    <td className="p-3 text-center text-muted-foreground">{item.on_duty}</td>
                    <td className="p-3 text-right text-foreground">
                      {formatCurrency(item.avg_daily_rate)}
                    </td>
                    <td className="p-3 text-right text-primary font-medium">
                      {formatCurrency(item.avg_monthly_rate)}
                    </td>
                    <td className="p-3 text-right text-muted-foreground">
                      {formatCurrency(item.total_value)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Breakdown by RAM */}
        <div className="glass-card p-4 sm:p-6 rounded-xl border border-border/40">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
            <HardDrive className="w-5 h-5 mr-2 text-green-500" />
            Detail Per RAM Capacity
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {byRAM.map((item, idx) => (
              <div key={idx} className="bg-muted/30 p-4 rounded-lg border border-border">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-lg font-bold text-foreground">{item._id}</h4>
                  <span className="px-3 py-1 bg-blue-500/10 text-blue-600 rounded-full text-sm font-medium">
                    {item.total_units} unit
                  </span>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Ready:</span>
                    <span className="text-green-600 font-medium">{item.ready} unit</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Rented:</span>
                    <span className="text-blue-600 font-medium">{item.rented} unit</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Avg Daily:</span>
                    <span className="text-foreground font-medium">{formatCurrency(item.avg_daily_rate)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Avg Monthly:</span>
                    <span className="text-primary font-medium">{formatCurrency(item.avg_monthly_rate)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Breakdown by Category */}
        <div className="glass-card p-4 sm:p-6 rounded-xl border border-border/40">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
            <Monitor className="w-5 h-5 mr-2 text-purple-500" />
            Detail Per Kategori Asset
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {byCategory.map((item, idx) => (
              <div key={idx} className="bg-muted/30 p-4 sm:p-6 rounded-lg border border-border">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-xl font-bold text-foreground capitalize">{item._id}</h4>
                  <span className="px-3 py-1 bg-blue-500/10 text-blue-600 rounded-full font-medium">
                    {item.total_units} unit
                  </span>
                </div>
                
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-green-500/10 p-3 rounded">
                    <p className="text-xs text-muted-foreground">Ready</p>
                    <p className="text-2xl font-bold text-green-600">{item.ready}</p>
                  </div>
                  <div className="bg-blue-500/10 p-3 rounded">
                    <p className="text-xs text-muted-foreground">Rented</p>
                    <p className="text-2xl font-bold text-blue-600">{item.rented}</p>
                  </div>
                  <div className="bg-purple-500/10 p-3 rounded">
                    <p className="text-xs text-muted-foreground">On Duty</p>
                    <p className="text-2xl font-bold text-purple-600">{item.on_duty}</p>
                  </div>
                  <div className="bg-yellow-500/10 p-3 rounded">
                    <p className="text-xs text-muted-foreground">Maintenance</p>
                    <p className="text-2xl font-bold text-yellow-600">{item.maintenance}</p>
                  </div>
                </div>

                <div className="border-t border-border pt-3 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Daily Rate (Avg):</span>
                    <span className="text-foreground font-medium">{formatCurrency(item.avg_daily_rate)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Weekly Rate (Avg):</span>
                    <span className="text-foreground font-medium">{formatCurrency(item.avg_weekly_rate)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Monthly Rate (Avg):</span>
                    <span className="text-primary font-medium">{formatCurrency(item.avg_monthly_rate)}</span>
                  </div>
                  <div className="flex justify-between text-sm border-t border-border pt-2">
                    <span className="text-muted-foreground">Total Investment:</span>
                    <span className="text-foreground font-bold">{formatCurrency(item.total_purchase_value)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Current Value:</span>
                    <span className="text-green-600 font-bold">{formatCurrency(item.total_value)}</span>
                  </div>
                </div>

                {item.brands && item.brands.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-border">
                    <p className="text-xs text-muted-foreground mb-2">Brands:</p>
                    <div className="flex flex-wrap gap-2">
                      {item.brands.map((brand, bidx) => (
                        <span key={bidx} className="px-2 py-1 bg-primary/10 text-primary rounded text-xs">
                          {brand}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Rental Pricing Analysis */}
        {pricingAnalysis && pricingAnalysis.length > 0 && (
          <div className="glass-card p-4 sm:p-6 rounded-xl border border-border/40">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
              <DollarSign className="w-5 h-5 mr-2 text-yellow-500" />
              Analisis Harga Rental Per Kategori Client
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {pricingAnalysis.map((item, idx) => (
                <div key={idx} className="bg-gradient-to-br from-primary/5 to-primary/10 p-6 rounded-lg border border-primary/30">
                  <h4 className="text-lg font-bold text-foreground mb-4 capitalize">
                    {item._id === 'company' ? '🏢 Perusahaan' : item._id === 'individual' ? '👤 Perorangan' : '🎉 Event'}
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-muted-foreground">Total Assets</p>
                      <p className="text-2xl font-bold text-foreground">{item.total_assets}</p>
                    </div>
                    <div className="border-t border-border pt-2">
                      <p className="text-xs text-muted-foreground">Daily Rate</p>
                      <p className="text-sm font-medium text-foreground">
                        Min: {formatCurrency(item.min_daily)} - Max: {formatCurrency(item.max_daily)}
                      </p>
                      <p className="text-lg font-bold text-primary">Avg: {formatCurrency(item.avg_daily)}</p>
                    </div>
                    <div className="border-t border-border pt-2">
                      <p className="text-xs text-muted-foreground">Monthly Average</p>
                      <p className="text-xl font-bold text-green-600">{formatCurrency(item.avg_monthly)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Export Actions */}
        <div className="flex flex-col sm:flex-row justify-center gap-4">
          <button
            onClick={() => window.print()}
            className="px-6 py-3 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors"
          >
            📄 Export ke PDF
          </button>
          <button
            onClick={() => navigate('/assets')}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            📦 Lihat Semua Assets
          </button>
        </div>
      </div>
    </Layout>
  );
};

export default AssetAnalyticsPage;
